import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { EMPTY, of, Subject, timer } from "rxjs";
import { catchError, delay, filter, switchMap, take, tap } from "rxjs/operators";

export enum SaveStatus {
    PendingSave,
    Saving,
    Saved,
    Error,
}

@Component({
    selector: "adapt-autosave",
    standalone: false,
    templateUrl: "./autosave.component.html",
    styleUrl: "./autosave.component.css",
})
export class AutosaveComponent extends BaseComponent implements OnInit {
    public readonly SaveStatus = SaveStatus;

    @Input() public messagePosition: "left" | "right" = "left";
    @Input() public pauseSaving = false;
    @Input() public saveDelay = 1_000;
    @Output() public saveComplete = new EventEmitter<void>();

    public saveStatus = SaveStatus.Saved;
    public saveStatusTextShown = true;
    public errorMessage?: string;

    public entitiesToConfirm: IBreezeEntity[] = [];
    private triggerSave$ = new Subject<void>();

    public constructor(private commonDataService: CommonDataService) {
        super();

        this.triggerSave$.pipe(
            tap(() => this.saveStatus = SaveStatus.PendingSave),
            switchMap(() => timer(this.saveDelay, this.saveDelay).pipe(
                filter(() => !this.pauseSaving),
                take(1),
            )),
            switchMap(() => this.triggerImmediateSave()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public ngOnInit() {
        this.hideStatusText();
    }

    public triggerSave(entitiesToConfirm: IBreezeEntity[]) {
        ArrayUtilities.addElementIfNotAlreadyExists(this.entitiesToConfirm, ...entitiesToConfirm);
        this.triggerSave$.next();
    }

    public triggerImmediateSave() {
        if (this.entitiesToConfirm.length === 0
            || !this.entitiesToConfirm.some((e) => e.entityAspect.entityState.isAddedModifiedOrDeleted())) {
            return EMPTY;
        }

        this.saveStatus = SaveStatus.PendingSave;
        return this.save(this.entitiesToConfirm);
    }

    @Autobind
    public save(entitiesToConfirm: IBreezeEntity[]) {
        return of(undefined).pipe(
            tap(() => this.saveStatus = SaveStatus.Saving),
            // always save items, just skip invalid ones
            switchMap(() => this.commonDataService.saveEntities(entitiesToConfirm.filter((e) => !e.entityAspect.hasValidationErrors))),
            delay(500),
            tap(() => {
                this.entitiesToConfirm = entitiesToConfirm.filter((e) => e.entityAspect.entityState.isAddedModifiedOrDeleted());

                this.errorMessage = undefined;
                this.saveStatus = SaveStatus.Saved;
                this.saveStatusTextShown = true;
                this.saveComplete.emit();
            }),
            tap(() => this.hideStatusText()),
            catchError((err) => {
                this.saveStatus = SaveStatus.Error;
                this.errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(err);
                this.log.error(this.errorMessage);
                // continue this observable
                return of();
            }),
            this.takeUntilDestroyed(),
        );
    }

    private hideStatusText() {
        setTimeout(() => this.saveStatusTextShown = false, 5_000);
    }
}
